import { useCallback, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { FiTrash2, FiEye, FiEyeOff, FiCamera } from "react-icons/fi";

function formatProjectionDate(createdAt) {
  if (!createdAt) return "";
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
}

// One row of the layer stack — modeled on SketchEditor/LayerPanel.jsx's
// SortableLayerRow. `{...attributes} {...listeners}` is spread only onto the
// grip handle, not the row, so the rest of the row (the "+ Camera"/delete
// buttons, the thumbnail) stays normally clickable during a drag gesture.
function SortableProjectionRow({
  projection,
  onToggleVisibility,
  onProjectAndMoveCamera,
  onDeleteProjection,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: projection._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const visible = projection.visible ?? true;

  return (
    <div
      ref={setNodeRef}
      style={style}
      id={`Scene3DProjectionRow-${projection._id}`}
      className={`flex items-center gap-2 border-b border-background bg-secondary/20 ${
        isDragging ? "shadow-md z-10" : ""
      } ${visible ? "" : "opacity-40"}`}>
      <button
        id={`Scene3DProjectionRow__dragHandle-${projection._id}`}
        type="button"
        title="Drag to reorder"
        className="shrink-0 p-1 rounded touch-none cursor-grab active:cursor-grabbing text-secondary-foreground/40 hover:text-secondary-foreground"
        {...attributes}
        {...listeners}>
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      {projection.thumbnailUrl && (
        <img
          src={projection.thumbnailUrl}
          alt=""
          className="w-10 h-10 object-cover flex-shrink-0"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-secondary-foreground truncate">
          {projection.name}
        </div>
        <div className="text-[9px] text-secondary-foreground/50 truncate">
          {projection.cameraName || "Camera"} ·{" "}
          {formatProjectionDate(projection.createdAt)}
        </div>
      </div>
      <div className="flex items-center gap-1 px-2 flex-shrink-0">
        <button
          id={`Scene3DProjectionRow__visibilityToggle-${projection._id}`}
          type="button"
          title={visible ? "Hide this layer" : "Show this layer"}
          onClick={() => onToggleVisibility?.(projection._id)}
          className="text-secondary-foreground/70 hover:text-white cursor-pointer p-1">
          {visible ? (
            <FiEye className="w-3.5 h-3.5" />
          ) : (
            <FiEyeOff className="w-3.5 h-3.5" />
          )}
        </button>
        <button
          id={`Scene3DProjectionRow__projectMoveCameraTrigger-${projection._id}`}
          type="button"
          title={
            projection.canMoveCamera
              ? "Move the camera to this projection's pose"
              : "Original camera no longer exists in this scene"
          }
          disabled={!projection.canMoveCamera}
          onClick={() => onProjectAndMoveCamera?.(projection)}
          className="text-secondary-foreground/70 hover:text-white cursor-pointer p-1">
          <FiCamera className="w-3 h-3" />
        </button>
        <button
          id={`Scene3DProjectionRow__deleteTrigger-${projection._id}`}
          type="button"
          title="Delete projection"
          onClick={() => onDeleteProjection?.(projection._id)}
          className="text-secondary-foreground/60 hover:text-destructive-foreground cursor-pointer p-1">
          <FiTrash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// Saved render-projections for the scene (Scene3D.js's projectionSchema) —
// treated as a layer stack: every visible one renders simultaneously,
// composited in this array's own order (top of this list = front-most/wins
// overlaps — see projection.js), draggable to reorder, each independently
// toggleable on/off.
export default function ProjectionsPanel({
  projections = [],
  onToggleVisibility,
  onReorderProjections,
  onProjectAndMoveCamera,
  onDeleteProjection,
}) {
  const sortableIds = useMemo(
    () => projections.map((p) => p._id),
    [projections],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return;
      const oldIndex = sortableIds.indexOf(active.id);
      const newIndex = sortableIds.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      onReorderProjections?.(arrayMove(sortableIds, oldIndex, newIndex));
    },
    [sortableIds, onReorderProjections],
  );

  return (
    <div
      id="Scene3DProjectionsPanel"
      className="flex flex-col py-2 overflow-y-auto flex-1 min-h-0">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}>
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}>
          {projections.map((projection) => (
            <SortableProjectionRow
              key={projection._id}
              projection={projection}
              onToggleVisibility={onToggleVisibility}
              onProjectAndMoveCamera={onProjectAndMoveCamera}
              onDeleteProjection={onDeleteProjection}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}
